import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { api, formatApiErrorDetail } from '../lib/api';
import { LayoutDashboard, FileText, AlertTriangle, Send, Building2, LogOut, HardDrive, Activity, Trash2, X, Loader2 } from 'lucide-react';

const NAV = [
  { to: '/', label: 'Panel', icon: LayoutDashboard, testid: 'nav-dashboard', end: true },
  { to: '/documents', label: 'Documentos', icon: FileText, testid: 'nav-documents' },
  { to: '/review', label: 'Por revisar', icon: AlertTriangle, testid: 'nav-review' },
  { to: '/accountant', label: 'Asesoría', icon: Send, testid: 'nav-accountant' },
  { to: '/wasabi', label: 'Wasabi', icon: HardDrive, testid: 'nav-wasabi' },
  { to: '/ingest-log', label: 'Registro ingesta', icon: Activity, testid: 'nav-ingest-log' },
  { to: '/companies', label: 'Empresas', icon: Building2, testid: 'nav-companies' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [typed, setTyped] = useState('');

  const doLogout = () => {
    logout();
    nav('/login');
  };

  const doReset = async () => {
    setResetting(true);
    const t = toast.loading('Borrando datos…');
    try {
      const { data } = await api.post('/api/admin/reset-data', { confirm: 'RESET' });
      const total = Object.values(data.rows_deleted || {}).reduce((a, b) => a + b, 0);
      toast.dismiss(t);
      toast.success('Datos borrados', {
        description: `${total} fila(s) eliminada(s). Empresas y admin preservados.`,
        duration: 6000,
      });
      setResetOpen(false);
      setTyped('');
      // Force reload to refresh all data in memory
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.dismiss(t);
      toast.error('Error al borrar', {
        description: formatApiErrorDetail(e?.response?.data?.detail) || e.message,
        duration: 10000,
      });
    } finally {
      setResetting(false);
    }
  };

  const closeModal = () => {
    if (resetting) return;
    setResetOpen(false);
    setTyped('');
  };

  return (
    <div className="min-h-screen flex bg-bg text-ink">
      {/* Sidebar */}
      <aside className="w-44 shrink-0 border-r border-ink/90 flex flex-col">
        <div className="px-4 py-5 border-b border-ink/90">
          <div className="text-[10px] tracking-wide2 uppercase font-bold text-muted">
            BluPlanet
          </div>
          <div className="text-lg font-semibold tracking-tight mt-1">Administracion</div>
        </div>
        <nav className="flex-1 py-4">
          {NAV.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 transition-colors duration-75 ${
                  isActive
                    ? 'border-accent font-semibold text-ink'
                    : 'border-transparent text-muted hover:text-ink hover:bg-surface'
                }`
              }
            >
              <Icon size={16} strokeWidth={1.75} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-ink/90 px-4 py-4">
          <div className="text-[10px] tracking-wide2 uppercase font-bold text-muted">
            Conectado como
          </div>
          <div className="text-sm font-medium truncate">{user?.email}</div>
          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              onClick={doLogout}
              data-testid="logout-btn"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide2 text-muted hover:text-danger"
            >
              <LogOut size={14} /> Salir
            </button>
            <button
              onClick={() => setResetOpen(true)}
              data-testid="reset-data-btn"
              title="Borrar todos los datos (excepto empresas y admin)"
              className="inline-flex items-center gap-1 p-1.5 text-muted hover:text-danger border border-transparent hover:border-danger transition-colors"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>

      {/* Reset confirm modal */}
      {resetOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid="reset-modal-overlay"
        >
          <div
            className="bg-white border-2 border-ink max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="reset-modal"
          >
            <div className="border-b-2 border-ink px-5 py-3 bg-danger text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle size={18} />
                <h2 className="text-sm font-bold uppercase tracking-wide2">Borrar todos los datos</h2>
              </div>
              <button onClick={closeModal} disabled={resetting} className="hover:opacity-70">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <p>
                Esta acción <strong>elimina permanentemente</strong> todos los datos transaccionales:
              </p>
              <ul className="text-xs font-mono border border-border bg-surface p-3 space-y-0.5">
                <li>· <strong>documents</strong> — todas las facturas registradas</li>
                <li>· <strong>accountant_batches</strong> — todo el histórico de envíos</li>
                <li>· <strong>document_ingest_attempts</strong> — registro de ingesta</li>
              </ul>
              <p className="text-xs">
                <strong>Se preservan</strong>: <code className="font-mono">companies</code>,{' '}
                <code className="font-mono">admin_users</code>.
              </p>
              <p className="text-xs text-muted">
                Los archivos en Wasabi <strong>NO</strong> se borran. Usa la página Wasabi para limpiarlos.
              </p>
              <div className="border-t border-border pt-3">
                <label className="block text-[10px] font-bold uppercase tracking-wide2 text-muted mb-1">
                  Escribe <code className="font-mono text-danger">RESET</code> para confirmar
                </label>
                <input
                  type="text"
                  data-testid="reset-confirm-input"
                  autoFocus={false}
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder="RESET"
                  disabled={resetting}
                  className="w-full px-3 py-2 bg-white border border-ink text-sm font-mono focus:outline-none focus:border-danger"
                />
              </div>
            </div>
            <div className="border-t-2 border-ink px-5 py-3 flex items-center justify-end gap-2 bg-surface">
              <button
                onClick={closeModal}
                disabled={resetting}
                data-testid="reset-cancel-btn"
                autoFocus
                className="px-4 py-2 text-xs font-bold uppercase tracking-wide2 border-2 border-ink bg-white hover:bg-ink hover:text-white transition-colors"
              >
                No, cancelar
              </button>
              <button
                onClick={doReset}
                disabled={resetting || typed !== 'RESET'}
                data-testid="reset-confirm-btn"
                className="px-4 py-2 text-xs font-bold uppercase tracking-wide2 border-2 border-danger bg-danger text-white hover:bg-white hover:text-danger transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {resetting ? (
                  <>
                    <Loader2 size={12} className="animate-spin" /> Borrando…
                  </>
                ) : (
                  <>
                    <Trash2 size={12} /> Sí, borrar todo
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
